import { AppContext } from 'context/ContextProvider';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FeePolicyEnforcerTemplate from 'views/shared/MgmtFeePolicyEnforcer/FeePolicyEnforcerTemplate';
import { MgmtFeeTypeEnum, MgmtViewsEnum } from 'views/shared/types';
import HowToPayModal from 'views/shared/HowToPay/HowToPayModal';
import { SettingsEventTypes } from 'types/AppType';
import ChargeAndSaveCard from 'views/shared/HowToPay/chargeAndSaveCard';
import PreferenceConfirmation from 'views/shared/MgmtFeePolicyEnforcer/preferenceConfirmation';
import Button from 'components/Button';
import { useMgmtFeeContext } from 'context/MgmtFeeContext';
import { ChargeMgmtAndSaveCardEnum } from 'types/commonTypes';
import { BackArrow } from 'assets/icons';
import { Dialog } from '@headlessui/react';
import { getHowToPayMgmtFeeAndAccruedOptions, getMgmtFeeAndAccruedOptions } from './helpers';
import StorageManagementFeePayment from './components/StorageManagementFeePayment';
import StorageFeePaymentDialog from './components/StorageFeePaymentDialog';

const CombineFeeModal = () => {
  const { enforceMgmtFeeRules } = useFlags();
  const { t } = useTranslation();
  const { formatter: currencyFormatter, gbpToTargetCurrency } = useContext(AppContext);
  const {
    state: {
      annualFeeAmount,
      forceAction,
      mgmtHashRef,
      monthlyFeeAmount,
      selectedFeeAmount,
      totalDueTodayAnnualFee,
      totalDueTodayMonthlyFee,
      viewType,
      selectedMgmtFeeType,
      accruedFee,
      hasMitCard,
    },
    onFeeTypeSubmit,
    onHowToPayFee,
    updateState,
  } = useMgmtFeeContext();

  const hasFees = selectedFeeAmount > 0;
  const isAddCard = !hasMitCard && !hasFees;

  const mgmtFeeAndAccruedOptions = useMemo(() => {
    return getMgmtFeeAndAccruedOptions({
      annualFeeAmount,
      monthlyFeeAmount,
      currencyFormatter,
      accruedFee,
      totalDueTodayAnnualFee,
      totalDueTodayMonthlyFee,
      t,
    });
  }, [
    annualFeeAmount,
    monthlyFeeAmount,
    currencyFormatter,
    totalDueTodayAnnualFee,
    totalDueTodayMonthlyFee,
    accruedFee,
    t,
  ]);

  const howToPayMgmtFeeAndAccruedOptions = useMemo(() => {
    return getHowToPayMgmtFeeAndAccruedOptions({
      currentAmount: selectedFeeAmount,
      currencyFormatter,
      t,
      selectedMgmtFeeType,
    });
  }, [selectedFeeAmount, t, currencyFormatter, selectedMgmtFeeType]);

  const hashRef = useMemo(() => {
    const data = encodeURIComponent(btoa(JSON.stringify(mgmtHashRef)));
    return {
      onSuccessHash: `#${ChargeMgmtAndSaveCardEnum.onChargeCardMgmtSuccess}=${data}`,
      onFailureHash: `#${ChargeMgmtAndSaveCardEnum.onChargeCardMgmtFailure}=${data}`,
    };
  }, [mgmtHashRef]);

  const onClose = () => {
    updateState({ showModal: false, viewType: MgmtViewsEnum.NONE });
  };

  const onSuccessClose = () => {
    updateState({ showModal: false, showBanner: false, onNavShowModel: false, viewType: MgmtViewsEnum.NONE });
  };

  const customHeader = useCallback(() => {
    return (
      <Dialog.Title as="h3" className="text-lg  flex-col font-medium leading-6 text-gray-900">
        <div className="text-14 font-medium leading-6 text-gray-900  space-x-3 flex items-center ">
          <BackArrow
            className="cursor-pointer text-14 z-10  top-4"
            onClick={() => {
              updateState({ viewType: MgmtViewsEnum.COMBINE_FEE_TYPE });
            }}
          />

          <div>Back</div>
        </div>

        <div className="w-full flex mt-5 flex-col items-center">
          <span className="text-20 font-medium leading-6 text-gray-900  space-x-3 flex items-center ">Add Card</span>
        </div>
      </Dialog.Title>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {enforceMgmtFeeRules && viewType === MgmtViewsEnum.COMBINE_FEE_TYPE && (
        <StorageManagementFeePayment />
        // <FeePolicyEnforcerTemplate
        //   eventType={MgmtViewsEnum.COMBINE_FEE_TYPE}
        //   accruedFee={accruedFee}
        //   options={mgmtFeeAndAccruedOptions}
        //   forceAction={forceAction}
        //   show
        //   onSubmit={(mgmtFeeType) => {
        //     onFeeTypeSubmit(mgmtFeeType as MgmtFeeTypeEnum);
        //   }}
        //   onClose={onClose}
        // />
      )}

      {enforceMgmtFeeRules && viewType === MgmtViewsEnum.COMBINE_FEE_PAYMENT_TYPE && (
        <HowToPayModal
          options={howToPayMgmtFeeAndAccruedOptions}
          forceAction={forceAction}
          eventType={SettingsEventTypes.UPDATE_MGMT_FEE_SETTINGS}
          isOpen
          propKey="howToPay"
          dbKey=""
          onClose={onClose}
          onSubmit={onHowToPayFee}
        />
      )}

      {enforceMgmtFeeRules && viewType === MgmtViewsEnum.CHARGE_AND_SAVE_CARD && (
        <StorageFeePaymentDialog
          customHeader={isAddCard ? customHeader() : null}
          btnText={isAddCard ? 'Add Card' : `PAY ${gbpToTargetCurrency.convertAndFormat(selectedFeeAmount)} NOW`}
          mgmtFeeType={selectedMgmtFeeType}
          title={isAddCard ? 'Add Card' : 'Make Payment'}
          show
          forceAction={forceAction}
          amount={selectedFeeAmount}
          transactionType=""
          onSuccessHash={hashRef.onSuccessHash}
          onFailureHash={hashRef.onFailureHash}
          onClose={forceAction ? undefined : onClose}
          onBack={() => {
            updateState({ viewType: MgmtViewsEnum.COMBINE_FEE_TYPE });
          }}
        />
      )}

      {enforceMgmtFeeRules && viewType === MgmtViewsEnum.CONFIRMATION && (
        <PreferenceConfirmation
          body={<div>{t('account:accruedFeePreference.confirmText')}</div>}
          show
          footer={
            <div className="w-full flex justify-center mt-3">
              <Button className="btn-primary" onClick={onSuccessClose}>
                {t('common:generalSuccess.buttonText')}
              </Button>
            </div>
          }
        />
      )}

      {((enforceMgmtFeeRules && viewType === MgmtViewsEnum.ADD_CARD_FAILED) ||
        (enforceMgmtFeeRules && viewType === MgmtViewsEnum.CHARGE_AND_SAVE_CARD_FAILED)) && (
        <PreferenceConfirmation
          body={<div>{t('account:payments.addCard.3DSecureFailure')}</div>}
          show
          footer={
            <div className="w-full flex justify-center mt-3">
              <Button className="btn-primary" onClick={onClose}>
                {t('common:generalSuccess.buttonText')}
              </Button>
            </div>
          }
        />
      )}
    </>
  );
};

export default CombineFeeModal;
